import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { api } from '../api';

type Status = 'idle' | 'uploading' | 'sending' | 'success' | 'error';

export default function ThankYou() {
    useSEO(
        'Wyślij Podziękowanie — PickOneGift',
        'Pokaż wdzięczność za prezent! Załącz zdjęcie i wyślij cyfrową kartkę z podziękowaniem.'
    );

    const { giftId } = useParams<{ giftId: string }>();
    const [message, setMessage] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imagePreview, setImagePreview] = useState('');
    const [status, setStatus] = useState<Status>('idle');
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Show local preview
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreview(ev.target?.result as string);
        reader.readAsDataURL(file);

        // Upload to S3 via presign
        try {
            setStatus('uploading');
            const { uploadUrl, publicUrl } = await api.presignUpload({
                filename: file.name,
                contentType: file.type,
            });

            await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file,
            });

            setImageUrl(publicUrl);
            setStatus('idle');
        } catch {
            setError('Nie udało się przesłać zdjęcia. Spróbuj ponownie.');
            setStatus('error');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!giftId || !message.trim()) return;

        setStatus('sending');
        setError('');

        try {
            await api.sendThankYou(giftId, {
                message: message.trim(),
                ...(imageUrl ? { imageUrl } : {}),
            });
            setStatus('success');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Coś poszło nie tak');
            setStatus('error');
        }
    };

    if (status === 'success') {
        return (
            <div className="page">
                <Header />
                <div className="container" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}>
                    <div className="card glass" style={{ textAlign: 'center', maxWidth: '520px', padding: '48px 32px' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>💌</div>
                        <h2>Podziękowanie wysłane!</h2>
                        <p className="text-muted" style={{ marginTop: '12px', lineHeight: 1.7 }}>
                            Twoja wiadomość została dostarczona do osoby, która przygotowała dla Ciebie listę prezentów.
                            Dziękujemy, że doceniasz czyjeś gesty!
                        </p>
                        <div style={{ marginTop: '32px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="/create" className="btn btn-primary">
                                🎁 Stwórz własną listę
                            </Link>
                            <Link to="/" className="btn btn-secondary">
                                Strona główna
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="page">
            <Header />

            {/* Hero */}
            <div style={{
                textAlign: 'center',
                padding: '64px 24px 32px',
                background: 'linear-gradient(180deg, rgba(74, 222, 128, 0.08) 0%, transparent 100%)',
            }}>
                <h1 style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
                    💌 Wyślij <span style={{ background: 'linear-gradient(135deg, #4ade80, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Podziękowanie</span>
                </h1>
                <p className="text-muted" style={{ fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto' }}>
                    Pokaż, jak bardzo cieszysz się z prezentu. Załącz zdjęcie i napisz kilka słów od serca.
                </p>
            </div>

            <div className="container" style={{ flex: 1, padding: '0 24px 80px' }}>
                <form
                    onSubmit={handleSubmit}
                    className="card glass"
                    style={{ maxWidth: '600px', margin: '0 auto', padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}
                >
                    {/* Photo Upload */}
                    <div className="form-group">
                        <label className="form-label">📸 Zdjęcie z prezentem (opcjonalne)</label>
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: '2px dashed var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                padding: imagePreview ? '0' : '40px 20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'border-color 0.25s ease',
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-primary)')}
                            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                        >
                            {imagePreview ? (
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={imagePreview}
                                        alt="Podgląd"
                                        style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', display: 'block' }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        padding: '12px',
                                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                        color: 'white',
                                        fontSize: '0.85rem',
                                    }}>
                                        Kliknij, aby zmienić zdjęcie
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📷</div>
                                    <p className="text-muted">Kliknij, aby dodać zdjęcie (JPEG, PNG, WebP)</p>
                                </>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleImageUpload}
                        />
                        {status === 'uploading' && (
                            <p style={{ color: 'var(--color-primary-light)', fontSize: '0.85rem', marginTop: '8px' }}>
                                ⏳ Przesyłanie zdjęcia...
                            </p>
                        )}
                    </div>

                    {/* Message */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="thank-you-message">✍️ Twoja wiadomość</label>
                        <textarea
                            id="thank-you-message"
                            className="form-textarea"
                            placeholder="Dziękuję Ci za ten wspaniały prezent! Jestem zachwycona..."
                            rows={5}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={1000}
                            required
                            disabled={status === 'sending'}
                        />
                        <span className="text-muted" style={{ fontSize: '0.8rem', textAlign: 'right' }}>
                            {message.length}/1000
                        </span>
                    </div>

                    {error && (
                        <div style={{
                            background: 'rgba(248, 113, 113, 0.1)',
                            border: '1px solid rgba(248, 113, 113, 0.3)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '12px 16px',
                            color: 'var(--color-error)',
                            fontSize: '0.9rem',
                        }}>
                            ⚠️ {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={status === 'sending' || status === 'uploading' || !message.trim()}
                        style={{ width: '100%' }}
                    >
                        {status === 'sending' ? '💌 Wysyłanie...' : '💌 Wyślij Podziękowanie'}
                    </button>
                </form>
            </div>

            <Footer />
        </div>
    );
}
